import { CurrencyEnum } from '@systemeio/types'
import { getCssRgbValue } from 'shared/utils/get-css-rgb-value'

/*
 * Every currency takes one color family and is drawn like any dashboard bar: the pale rung at rest,
 * the solid one under the cursor. The currencies below keep a reserved family and anything else
 * takes what is left over, so no two series share a color.
 */
const COLOR_FAMILY_VARIABLES = {
  blue: '--color-system-primary',
  green: '--color-system-success',
  yellow: '--color-system-warning',
  red: '--color-system-error',
} as const

type AffiliateChartColorFamily = keyof typeof COLOR_FAMILY_VARIABLES

const RESERVED_COLOR_FAMILIES: Partial<Record<CurrencyEnum, AffiliateChartColorFamily>> = {
  [CurrencyEnum.EURO]: 'blue',
  [CurrencyEnum.US_DOLLAR]: 'green',
  [CurrencyEnum.BRAZILIAN_REAL]: 'red',
}

const getColorFamily = (
  currency: CurrencyEnum,
  allCurrencies: CurrencyEnum[],
): AffiliateChartColorFamily => {
  const reserved = RESERVED_COLOR_FAMILIES[currency]

  if (reserved) return reserved

  const available = (Object.keys(COLOR_FAMILY_VARIABLES) as AffiliateChartColorFamily[]).filter(
    family => !allCurrencies.some(cur => RESERVED_COLOR_FAMILIES[cur] === family),
  )
  const currencyIndex = allCurrencies.indexOf(currency)

  return available[Math.floor(Number(`0.${currencyIndex}`) * available.length)] ?? 'yellow'
}

export const getAffiliateChartColors = (currency: CurrencyEnum, allCurrencies: CurrencyEnum[]) => {
  const variable = COLOR_FAMILY_VARIABLES[getColorFamily(currency, allCurrencies)]

  return {
    fill: getCssRgbValue(`${variable}-200`),
    hover: getCssRgbValue(`${variable}-500`),
  }
}

export const getAffiliateChartTextColor = (
  currency: CurrencyEnum,
  allCurrencies: CurrencyEnum[],
) => ({ color: getAffiliateChartColors(currency, allCurrencies).hover })
